import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { loadTossPayments } from '@tosspayments/payment-sdk';
import api from '../../api/axiosConfig';

interface Props {
  open: boolean;
  onClose: () => void;
  userId?: number | string;
}

interface TossConfigRes {
  clientKey: string;
  environment: string;
  successUrl: string;
  failUrl: string;
  plan: string;
  amount: number;
  currency: string;
}

interface PrepareBillingRes {
  invoiceId: number;
  subscriptionId: number;
  userId: number;
  amount: number;
  currency: string;
  orderId: string;
  orderName?: string;
  status?: string;
  provider?: string;
}

const BILLING_CUSTOMER_KEY_STORAGE = 'policyIntel:billingCustomerKey';
const BILLING_AUTH_CALLBACK_PATH = '/billing/billing-auth/callback';

function createRandomCustomerKey(userId: number | string): string {
  const suffix =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Date.now()}${Math.random().toString(16).slice(2)}`;

  // 이메일, 전화번호, 순차 userId 단독 사용을 피하고 무작위 접미사를 붙인다.
  return `pi_${String(userId)}_${suffix}`.slice(0, 300);
}

function loadOrCreateCustomerKey(userId: number | string): string {
  const storageKey = `${BILLING_CUSTOMER_KEY_STORAGE}:${String(userId)}`;
  const saved = window.localStorage.getItem(storageKey)?.trim();
  if (saved) {
    return saved;
  }

  const created = createRandomCustomerKey(userId);
  window.localStorage.setItem(storageKey, created);
  return created;
}

function appendQuery(url: string, params: Record<string, string>): string {
  const parsed = new URL(url, window.location.origin);
  Object.entries(params).forEach(([key, value]) => parsed.searchParams.set(key, value));
  return parsed.toString();
}

const BillingPayDialog: React.FC<Props> = ({ open, onClose, userId }) => {
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState<TossConfigRes | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let mounted = true;

    const fetchConfig = async () => {
      try {
        setConfigLoading(true);
        setError('');
        const { data } = await api.get<TossConfigRes>('/billing/toss/config');
        if (mounted) {
          setConfig(data);
        }
      } catch (e: any) {
        if (mounted) {
          setError(
            e?.response?.data?.message ||
              e?.message ||
              '자동결제 설정을 불러오지 못했습니다.',
          );
        }
      } finally {
        if (mounted) {
          setConfigLoading(false);
        }
      }
    };

    void fetchConfig();

    return () => {
      mounted = false;
    };
  }, [open]);

  const displayAmount = useMemo(() => {
    return typeof config?.amount === 'number'
      ? `${config.amount.toLocaleString('ko-KR')}원`
      : '-';
  }, [config?.amount]);

  const handleRegisterCard = useCallback(async () => {
    setError('');

    if (!userId) {
      setError('사용자 정보가 없습니다. 다시 로그인해 주세요.');
      return;
    }

    if (!config?.clientKey?.trim()) {
      setError('Toss clientKey가 비어 있습니다.');
      return;
    }

    try {
      setLoading(true);

      const numericUserId =
        typeof userId === 'number' ? userId : Number(String(userId).trim());

      if (!Number.isFinite(numericUserId) || numericUserId <= 0) {
        throw new Error('사용자 식별값이 올바르지 않습니다.');
      }

      const customerKey = loadOrCreateCustomerKey(userId);
      const tossPayments = await loadTossPayments(config.clientKey.trim());

      // 신규 사용자도 subscription / invoice / payment 준비 레코드를 먼저 만든다.
      // 이 단계가 성공해야만 Toss 카드 인증으로 이동한다.
      const { data: prepared } = await api.post<PrepareBillingRes>('/billing/prepare', {
        userId: numericUserId,
        // DB/백엔드 PlanCode enum의 유료 플랜 식별자는 PAID다.
        // config.plan='pro'는 화면 표시용 값이므로 enum 값으로 직접 보내지 않는다.
        planCode: 'PAID',
        provider: 'toss',
        method: 'CARD',
        returnBaseUrl: `${window.location.origin}/mypage?tab=billing`,
      });

      if (!prepared?.orderId || !prepared?.invoiceId || !prepared?.subscriptionId) {
        throw new Error('결제 준비 정보를 생성하지 못했습니다.');
      }

      const callbackBaseUrl = new URL(
        BILLING_AUTH_CALLBACK_PATH,
        window.location.origin,
      ).toString();

      const successUrl = appendQuery(callbackBaseUrl, {
        flow: 'billing_auth',
        customerKey,
      });
      const failUrl = appendQuery(callbackBaseUrl, {
        flow: 'billing_auth',
        customerKey,
      });

      await tossPayments.requestBillingAuth('카드', {
        customerKey,
        successUrl,
        failUrl,
      });
    } catch (e: any) {
      console.error(e);
      const message =
        e?.response?.data?.message ||
        e?.message ||
        (String(e).toLowerCase().includes('cancel')
          ? '사용자가 카드 등록을 취소했습니다.'
          : '자동결제 카드 등록창을 열지 못했습니다.');
      setError(message);
      setLoading(false);
    }
  }, [config, userId]);

  const handleClose = useCallback(() => {
    if (loading || configLoading) {
      return;
    }
    setError('');
    onClose();
  }, [configLoading, loading, onClose]);

  const canRegister = Boolean(userId && config && !loading && !configLoading);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Pro 자동결제 등록</DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.65 }}>
          카드 인증 후 토스 빌링키를 서버에 안전하게 등록합니다. 테스트 환경에서는 1일 주기로
          자동결제 대상 여부를 확인합니다.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {configLoading ? (
          <Box sx={{ py: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">자동결제 설정 불러오는 중…</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography variant="body2">
              플랜: <b>{config?.plan || 'pro'}</b>
            </Typography>
            <Typography variant="body2">
              결제 금액: <b>{displayAmount}</b>
            </Typography>
            <Typography variant="body2">
              통화: <b>{config?.currency || 'KRW'}</b>
            </Typography>
            <Typography variant="body2">
              결제수단: <b>카드 자동결제</b>
            </Typography>
            <Alert severity="info" sx={{ mt: 1 }}>
              계좌이체와 가상계좌는 빌링키 자동결제 등록 대상에서 제외했습니다.
            </Alert>
          </Box>
        )}

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading || configLoading} variant="outlined">
          닫기
        </Button>
        <Button onClick={handleRegisterCard} disabled={!canRegister} variant="contained">
          {loading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <CircularProgress size={16} /> 카드 등록창 여는 중…
            </span>
          ) : (
            '자동결제 카드 등록'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingPayDialog;
